import { TraineeCertification } from '@common/types/certification.types'
import Button from '@components/arthurBased/inputs/Button'
import Icon from '@components/dataDisplay/Icon'
import Loader from '@components/state/Loader'
import { GREY_COLORS } from '@config/theme/colors'
import { FontWeight } from '@config/theme/sizes'
import { AddCircleOutlineRounded } from '@mui/icons-material'
import { Divider, Stack, styled, Typography } from '@mui/material'
import { useCertificationApi } from '@services/api/certification.api'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'
import CertificationCreationPopIn from './CertificationCreationPopIn'
import CertificationRow from './CertificationRow'
import CertificationUpdatePopIn from './CertificationUpdatePopIn'

/**
 * Trainee certifications
 * @constructor
 */
const TraineeCertifications = () => {
	const { getTraineeCertifications } = useCertificationApi()
	const { id: traineeId } = useParams()
	const [isCertificationCreationPopInOpen, setIsCertificationCreationPopInOpen] = useState(false)
	const [selectedCertification, setSelectedCertification] = useState<TraineeCertification>()

	const { data: certifications, isLoading, refetch } = useQuery({
		queryKey: ['getTraineeCertifications', traineeId],
		queryFn: () => traineeId ? getTraineeCertifications(traineeId) : undefined
	})

	if (isLoading) {
		return <Loader />
	}

	return (
		<Container>
			<Header>
				<Typography variant="h4" fontWeight={FontWeight.SEMI_BOLD}>
					<FormattedMessage id="trainee.certifications.header.title" />
				</Typography>
				<Button
					onClick={() => setIsCertificationCreationPopInOpen(true)}
				>
					<CreationButton>
						<FormattedMessage id="trainee.certifications.actions.create.label" />
						<Icon IconElement={AddCircleOutlineRounded} />
					</CreationButton>
				</Button>
			</Header>
			<List>
				{certifications
					? (
						certifications.map((certification, index) => (
							<Stack key={certification.id}>
								{index !== 0 && <Divider />}
								<CertificationRow
									certification={certification}
									setSelectedCertification={setSelectedCertification}
									traineeId={traineeId}
								/>
							</Stack>
						))
					)
					: (
						<NoData>
							<Typography>
								<FormattedMessage id="trainee.certifications.list.noData" />
							</Typography>
						</NoData>
					)
				}
			</List>
			{(isCertificationCreationPopInOpen && traineeId) && (
				<CertificationCreationPopIn
					traineeId={traineeId}
					onClose={() => setIsCertificationCreationPopInOpen(false)}
					onSuccess={() => {
						setIsCertificationCreationPopInOpen(false)
						refetch()
					}}
					isOpen
				/>
			)}
			{(selectedCertification && traineeId) && (
				<CertificationUpdatePopIn
					certification={selectedCertification}
					onClose={() => setSelectedCertification(undefined)}
					onSuccess={() => {
						setSelectedCertification(undefined)
						refetch()
					}}
					traineeId={traineeId}
					isOpen
				/>
			)}
		</Container>
	)
}

const Container = styled(Stack)({
	flex: 1,
	rowGap: '16px'
})

const Header = styled(Stack)({
	alignItems: 'center',
	flexDirection: 'row',
	justifyContent: 'space-between'
})

const CreationButton = styled(Stack)({
	alignItems: 'center',
	columnGap: '8px',
	flexDirection: 'row'
})

const List = styled(Stack)({
	border: `1px solid ${GREY_COLORS[100]}`,
	borderRadius: '8px',
	flex: 1,
	overflowY: 'scroll'
})

const NoData = styled(Stack)({
	alignItems: 'center',
	flex: 1,
	justifyContent: 'center'
})

export default TraineeCertifications
